import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBoard, updateBoard, getBoardHistory, getBoardDay, saveBoardDay, deleteBoardDay, getMissingBoardDays } from '../utils/api';
import toast from 'react-hot-toast';

const money0 = (v) => isFinite(v) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v) : '$0';
const numv = (s) => { const n = parseFloat(String(s).replace(/[^0-9.\-]/g, '')); return isFinite(n) ? n : 0; };

function workdays() { // Mon–Sat this month (exclude Sundays)
  const now = new Date(), y = now.getFullYear(), m = now.getMonth(), dim = new Date(y, m + 1, 0).getDate(), today = now.getDate();
  let total = 0, elapsed = 0, left = 0;
  for (let d = 1; d <= dim; d++) { const dow = new Date(y, m, d).getDay(); if (dow !== 0) { total++; if (d < today) elapsed++; else left++; } }
  return { total, elapsed, left };
}
function derive(b) {
  const wd = workdays();
  const dlo = b.days_left_override;
  const daysLeft = (dlo !== null && dlo !== undefined && dlo !== '') ? Number(dlo) : wd.left;
  const elapsed = Math.max(0, wd.total - daysLeft);
  const dailyGoal = daysLeft > 0 ? (numv(b.monthly_goal) - numv(b.sold_this_month)) / daysLeft : 0;
  const carry = Math.max(0, numv(b.lead_base) - numv(b.lead_prev));
  const leadToday = numv(b.lead_base) + carry;
  const avg = elapsed > 0 ? numv(b.sold_this_month) / elapsed : 0;
  const pacing = Math.round(avg * wd.total);
  const diff = pacing - numv(b.monthly_goal);
  return { daysLeft, dailyGoal: Math.max(0, dailyGoal), carry, leadToday, avg, pacing, diff, ahead: diff >= 0, totalDays: wd.total };
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Inter:wght@400;500;600;700;800&display=swap');
.pb-wrap{--bg1:#f7f2e7;--bg2:#efe7d6;--card:#fffdf8;--edge:rgba(201,146,42,.24);--ink:#172235;--muted:#7f848b;--line:rgba(23,53,41,.09);--gold:#c9922a;--gold-d:#a9791d;--green:#1d3b6c;--red:#c0392b;--serif:'Cormorant Garamond',Georgia,serif;--sans:'Inter',-apple-system,system-ui,sans-serif;
  font-family:var(--sans);background:linear-gradient(180deg,var(--bg1),var(--bg2));color:var(--ink);min-height:calc(100vh - 40px);border-radius:20px;padding:24px 18px}
.pb-inner{max-width:1000px;margin:0 auto}
.pb-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;flex-wrap:wrap;gap:10px}
.pb-e{color:var(--gold);font-weight:700;letter-spacing:.2em;font-size:.62rem;margin-bottom:3px}
.pb-t{font-family:var(--serif);font-weight:700;font-size:2rem;line-height:1}
.pb-date{color:var(--muted);font-size:.85rem}
.pb-btn{border:0;border-radius:980px;padding:10px 18px;font-weight:700;cursor:pointer;font-family:inherit;font-size:.9rem}
.pb-btn.edit{background:linear-gradient(120deg,var(--gold),var(--gold-d));color:#141b26}
.pb-btn.save{background:var(--green);color:#fff}
.pb-btn.cancel{background:#efe7d4;color:var(--ink);margin-right:8px}
.pb-note{font-size:.78rem;color:var(--muted);margin:2px 0 16px}
.pb-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.pb-tile{background:var(--card);border:1px solid var(--edge);border-radius:20px;padding:20px 22px;box-shadow:0 14px 34px -18px rgba(20,40,25,.4)}
.pb-tile.pb-editing{border-color:var(--gold)}
.pb-lbl{color:var(--gold);font-weight:800;letter-spacing:.12em;font-size:.72rem;text-transform:uppercase}
.pb-big{font-family:'Fraunces',Georgia,serif;font-weight:800;font-size:2.9rem;letter-spacing:-.02em;line-height:1.05;margin-top:8px;font-variant-numeric:tabular-nums}
.pb-big.money{color:var(--green)}
.pb-sub{color:var(--muted);font-size:.9rem;margin-top:8px;font-weight:500}
.pb-sub b{color:var(--ink);font-weight:700}
.pb-pill{display:inline-block;font-weight:700;font-size:.72rem;padding:3px 10px;border-radius:980px;margin-left:4px}
.pb-pill.up{background:rgba(30,107,79,.12);color:var(--green)}
.pb-pill.down{background:#fdece9;color:var(--red)}
.pb-bar{height:12px;border-radius:8px;background:#eeeff1;overflow:hidden;margin-top:12px}
.pb-bar i{display:block;height:100%;border-radius:8px;transition:width .5s}
.pb-prev{display:flex;justify-content:space-between;border-top:1px solid var(--line);margin-top:14px;padding-top:12px;font-size:.92rem}
.pb-prev .k{color:var(--muted);font-weight:500}.pb-prev .v{font-weight:800;font-family:'Fraunces',Georgia,serif;font-variant-numeric:tabular-nums}
.pb-ed{display:flex;align-items:center;gap:6px;margin-top:12px;flex-wrap:wrap}
.pb-ed label{font-size:.74rem;color:var(--muted);font-weight:600}
.pb-ed input{border:1.5px solid var(--edge);border-radius:8px;padding:6px 9px;font:inherit;font-weight:700;width:120px;color:var(--ink);outline:0;background:#fff}
@media(max-width:760px){.pb-grid{grid-template-columns:1fr}.pb-big{font-size:2.4rem}.pb-t{font-size:1.6rem}}
.pb-tabs{display:flex;gap:8px;margin:2px 0 18px}
.pb-tab{font-family:var(--sans);font-size:.9rem;font-weight:700;padding:8px 20px;border-radius:999px;border:1px solid var(--edge);background:var(--card);color:var(--muted);cursor:pointer;transition:all .15s}
.pb-tab:hover{color:var(--ink)}
.pb-tab.on{background:var(--green);color:#fff;border-color:var(--green)}
.pb-hist{background:var(--card);border:1px solid var(--edge);border-radius:20px;padding:6px;box-shadow:0 14px 34px -18px rgba(20,40,25,.4);overflow-x:auto}
.pb-htable{width:100%;border-collapse:collapse;font-family:var(--sans);font-size:.9rem}
.pb-htable th{text-align:left;color:var(--muted);font-weight:700;font-size:.7rem;letter-spacing:.04em;text-transform:uppercase;padding:13px 14px;border-bottom:2px solid var(--line);white-space:nowrap}
.pb-htable td{padding:12px 14px;border-bottom:1px solid var(--line);color:var(--ink);white-space:nowrap;font-variant-numeric:tabular-nums}
.pb-htable tr:last-child td{border-bottom:0}
.pb-htable td.d{font-weight:700;color:var(--green)}
.pb-htable td.rd{color:var(--red);font-weight:700}
.pb-htable tbody tr:hover td{background:rgba(201,146,42,.05)}
.pb-miss{background:#fff8e6;border:1px solid #f0d99a;border-radius:12px;padding:13px 16px;margin-bottom:16px;font-family:var(--sans);font-size:.9rem;color:#6b4e10}
.pb-miss b{font-weight:800}
.pb-chip{display:inline-block;margin:5px 6px 0 0;padding:5px 12px;border-radius:999px;border:1px solid #e0b24a;background:#fff;color:#8a6414;font-weight:700;font-size:.82rem;cursor:pointer;font-family:var(--sans)}
.pb-chip:hover{background:#e0b24a;color:#fff}
.pb-addbtn{font-family:var(--sans);font-size:.85rem;font-weight:700;padding:7px 15px;border-radius:999px;border:1px solid var(--green);background:var(--card);color:var(--green);cursor:pointer}
.pb-addbtn:hover{background:var(--green);color:#fff}
.pb-rowbtn{font-family:var(--sans);font-size:.78rem;font-weight:700;padding:4px 11px;border-radius:7px;border:1px solid var(--edge);background:var(--card);color:var(--muted);cursor:pointer}
.pb-rowbtn:hover{border-color:var(--green);color:var(--green)}
.pb-modal{position:fixed;inset:0;background:rgba(12,28,22,.45);display:flex;align-items:center;justify-content:center;padding:18px;z-index:900}
.pb-sheet{background:var(--card);border-radius:16px;max-width:560px;width:100%;max-height:92vh;overflow:auto;padding:22px 24px;font-family:var(--sans);box-shadow:0 18px 50px rgba(0,0,0,.28)}
.pb-sheet h3{font-family:'Fraunces',Georgia,serif;font-size:1.3rem;margin:0 0 4px;color:var(--ink)}
.pb-sheet .sub{color:var(--muted);font-size:.86rem;margin-bottom:16px}
.pb-fg{display:grid;grid-template-columns:1fr 1fr;gap:11px 14px}
.pb-fg label{display:block;font-size:.73rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px}
.pb-fg input{width:100%;padding:8px 10px;border:1px solid var(--edge);border-radius:8px;font-family:var(--sans);font-size:.92rem;font-variant-numeric:tabular-nums;box-sizing:border-box}
.pb-fg input:focus{outline:none;border-color:var(--green)}
.pb-acts{display:flex;gap:9px;margin-top:20px;flex-wrap:wrap}
.pb-acts .sp{flex:1}
@media(max-width:560px){.pb-fg{grid-template-columns:1fr}}
.pb-histnote{background:var(--card);border:1px solid var(--edge);border-radius:20px;padding:28px;text-align:center;color:var(--muted);font-family:var(--sans)}
`;

const DAY_FIELDS = [
  ['sold_this_month', 'Sold this month $'], ['sales_prev_day', 'Sold that day $'],
  ['lead_base', 'Lead base'], ['lead_prev', 'Leads that day'],
  ['lead_inbound', 'Inbound leads'], ['lead_cancelled', 'Cancelled leads'],
  ['pitch_leads', 'Leads to pitch'], ['pitch_pitched', 'Pitched'],
  ['close_closed', 'Closed'], ['monthly_goal', 'Monthly goal $'],
  ['pitch_goal', 'Pitch goal (0-1)'], ['close_goal', 'Close goal (0-1)'],
];

function todayISO() {
  try { return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }); }
  catch { return new Date().toISOString().slice(0, 10); }
}

function DayEditor({ date, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    getBoardDay(date)
      .then(r => { if (!live) return; setExists(r.exists); setF(r.entry || {}); })
      .catch(() => { toast.error('Could not load that day'); onClose(); });
    return () => { live = false; };
  }, [date, onClose]);

  const set = (k, v) => setF(o => ({ ...o, [k]: v }));

  async function save() {
    setBusy(true);
    try {
      const payload = {};
      DAY_FIELDS.forEach(([k]) => { payload[k] = f[k] ?? ''; });
      await saveBoardDay(date, payload);
      toast.success(exists ? `${date} updated` : `${date} added`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save that day');
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm(`Remove the entry for ${date}? This cannot be undone.`)) return;
    setBusy(true);
    try { await deleteBoardDay(date); toast.success(`${date} removed`); onSaved(); }
    catch { toast.error('Could not remove that day'); }
    finally { setBusy(false); }
  }

  return (
    <div className="pb-modal" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pb-sheet">
        <h3>{exists ? 'Edit' : 'Add'} {date}</h3>
        <div className="sub">
          {exists
            ? 'Correcting a day that was already recorded.'
            : 'This day was never recorded. Goals are carried over from the last recorded day — fill in the daily numbers.'}
          {' '}The live board is not affected.
        </div>
        {!f ? <div style={{ color: '#7f848b' }}>Loading…</div> : (
          <>
            <div className="pb-fg">
              {DAY_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label>{label}</label>
                  <input value={f[k] ?? ''} onChange={e => set(k, e.target.value)} placeholder="—" />
                </div>
              ))}
            </div>
            <div className="pb-acts">
              <button className="pb-addbtn" onClick={save} disabled={busy}
                style={{ background: 'var(--green)', color: '#fff' }}>
                {busy ? 'Saving…' : (exists ? 'Save changes' : 'Add this day')}
              </button>
              <button className="pb-rowbtn" onClick={onClose} disabled={busy}>Cancel</button>
              <span className="sp" />
              {exists && <button className="pb-rowbtn" onClick={remove} disabled={busy}
                style={{ color: 'var(--red)', borderColor: 'var(--red)' }}>Remove</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function HistoryPanel({ rows, missing, onEdit, onAdd }) {
  const pctv = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
  const today = (() => {
    try { return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }); }
    catch { return new Date().toISOString().slice(0, 10); }
  })();

  return (
    <div className="pb-hist">
      {missing && missing.length > 0 && (
        <div className="pb-miss">
          <b>{missing.length} working {missing.length === 1 ? 'day has' : 'days have'} no numbers recorded.</b>
          {' '}Click a date to fill it in.
          <div>{missing.map(d => (
            <button key={d} className="pb-chip" onClick={() => onEdit(d)}>
              {d}{d === today ? ' (today)' : ''}
            </button>
          ))}</div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="pb-addbtn" onClick={onAdd}>+ Add or edit a day</button>
        <span style={{ fontFamily: 'var(--sans)', fontSize: '.82rem', color: 'var(--muted)' }}>
          Every board save records that day automatically. Use this to catch up a missed day or fix a mistake.
        </span>
      </div>

      {rows === null ? <div className="pb-histnote">Loading history…</div>
        : !rows.length ? <div className="pb-histnote">No history yet. Each time the board is saved, that day&apos;s numbers are recorded here.</div>
        : (
        <table className="pb-htable">
          <thead>
            <tr>
              <th>Date</th><th>Sold (month)</th><th>Day $</th><th>Leads</th>
              <th>Inbound</th><th>Cancelled</th><th>Pitched</th><th>Pitch %</th>
              <th>Closed</th><th>Close %</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.snapshot_date}>
                <td className="d">{r.snapshot_date}</td>
                <td>{money0(r.sold_this_month || 0)}</td>
                <td>{money0(r.sales_prev_day || 0)}</td>
                <td>{r.lead_prev || 0}</td>
                <td>{r.lead_inbound || 0}</td>
                <td className={r.lead_cancelled > 0 ? 'rd' : ''}>{r.lead_cancelled || 0}</td>
                <td>{r.pitch_pitched || 0}</td>
                <td>{pctv(r.pitch_pitched || 0, r.pitch_leads || 0)}%</td>
                <td>{r.close_closed || 0}</td>
                <td>{pctv(r.close_closed || 0, r.pitch_pitched || 0)}%</td>
                <td><button className="pb-rowbtn" onClick={() => onEdit(r.snapshot_date)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function PerformanceBoard() {
  const { isAdmin } = useAuth();
  const [board, setBoard] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('board');
  const [history, setHistory] = useState(null);
  const [missing, setMissing] = useState(null);
  const [dayEdit, setDayEdit] = useState(null);   // 'YYYY-MM-DD' being edited

  useEffect(() => {
    const id = 'cpr-serif-fonts';
    if (!document.getElementById(id)) {
      const l = document.createElement('link');
      l.id = id; l.rel = 'stylesheet';
      l.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Inter:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => { getBoard().then(setBoard).catch(() => toast.error('Could not load board')); }, []);
  const loadHistory = useCallback(() => {
    getBoardHistory().then(setHistory).catch(() => setHistory([]));
    getMissingBoardDays().then(r => setMissing(r.missing || [])).catch(() => setMissing([]));
  }, []);

  useEffect(() => {
    if (tab === 'history' && isAdmin && history === null) loadHistory();
  }, [tab, isAdmin, history, loadHistory]);

  if (!board) return <div style={{ padding: 40, textAlign: 'center', color: '#7f848b' }}>Loading board…</div>;

  const view = edit ? form : board;
  const d = derive(view);
  const set = (k, val) => setForm(f => ({ ...f, [k]: val }));

  function startEdit() { setForm({ ...board }); setEdit(true); }
  function cancelEdit() { setEdit(false); setForm(null); }
  async function save() {
    setSaving(true);
    try { const updated = await updateBoard(form); setBoard(updated); setEdit(false); setForm(null); toast.success('Board updated'); }
    catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  }

  const pg = numv(view.pitch_goal);
  const pitchLeads = numv(view.pitch_leads), pitched = numv(view.pitch_pitched);
  const pa = pitchLeads > 0 ? Math.round(pitched / pitchLeads * 100) : 0;
  const cg = numv(view.close_goal);
  const closed = numv(view.close_closed);
  const ca = pitched > 0 ? Math.round(closed / pitched * 100) : 0;
  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="pb-wrap">
      <style>{CSS}</style>
      <div className="pb-inner">
        <div className="pb-top">
          <div>
            <div className="pb-e">AMANDA'S HOME IMPROVEMENT</div>
            <div className="pb-t">Performance Board</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="pb-date">{dateStr}</div>
            {isAdmin && !edit && <button className="pb-btn edit" onClick={startEdit}>Edit board</button>}
            {isAdmin && edit && (
              <div style={{ marginTop: 6 }}>
                <button className="pb-btn cancel" onClick={cancelEdit}>Cancel</button>
                <button className="pb-btn save" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            )}
          </div>
        </div>
        <div className="pb-note">
          {edit ? 'Editing — change the numbers and Save. Everyone sees the same board.'
            : 'Live board — visible to all reps, managers, canvassers & telemarketers.' + (isAdmin ? ' Only admins can edit.' : '')}
        </div>

        {isAdmin && (
          <div className="pb-tabs">
            <button className={`pb-tab${tab === 'board' ? ' on' : ''}`} onClick={() => setTab('board')}>Board</button>
            <button className={`pb-tab${tab === 'history' ? ' on' : ''}`} onClick={() => setTab('history')}>History</button>
          </div>
        )}

        {tab === 'board' && <div className="pb-grid">
          {/* DAILY GOAL */}
          <div className={`pb-tile${edit ? ' pb-editing' : ''}`}>
            <div className="pb-lbl">Daily Goal</div>
            <div className="pb-big money">{money0(d.dailyGoal)}</div>
            <div className="pb-sub">To hit the monthly target across the working days left.</div>
            <div className="pb-prev"><span className="k">Yesterday sold</span><span className="v">{money0(numv(view.sales_prev_day))}</span></div>
            {edit && <div className="pb-ed">
              <label>Sold this month $</label><input value={form.sold_this_month ?? ''} onChange={e => set('sold_this_month', e.target.value)} />
              <label>Yesterday $</label><input value={form.sales_prev_day ?? ''} onChange={e => set('sales_prev_day', e.target.value)} />
            </div>}
          </div>

          {/* LEAD COUNT */}
          <div className={`pb-tile${edit ? ' pb-editing' : ''}`}>
            <div className="pb-lbl">Lead Count</div>
            <div className="pb-big">{d.leadToday}</div>
            <div className="pb-sub">leads to hit today{d.carry > 0 && <span className="pb-pill down">+{d.carry} carried over</span>}</div>
            <div className="pb-prev"><span className="k">Base goal / day</span><span className="v">{numv(view.lead_base)}</span></div>
            <div className="pb-prev"><span className="k">Yesterday's leads</span><span className="v">{numv(view.lead_prev)}</span></div>
            <div className="pb-prev"><span className="k">Inbound</span><span className="v">{numv(view.lead_inbound)}</span></div>
            <div className="pb-prev"><span className="k">Cancelled</span><span className="v" style={{ color: numv(view.lead_cancelled) > 0 ? 'var(--red)' : undefined }}>{numv(view.lead_cancelled)}</span></div>
            {edit && <div className="pb-ed">
              <label>Base/day</label><input value={form.lead_base ?? ''} onChange={e => set('lead_base', e.target.value)} />
              <label>Yesterday</label><input value={form.lead_prev ?? ''} onChange={e => set('lead_prev', e.target.value)} />
              <label>Inbound</label><input value={form.lead_inbound ?? ''} onChange={e => set('lead_inbound', e.target.value)} />
              <label>Cancelled</label><input value={form.lead_cancelled ?? ''} onChange={e => set('lead_cancelled', e.target.value)} />
            </div>}
          </div>

          {/* PITCH % */}
          <div className={`pb-tile${edit ? ' pb-editing' : ''}`}>
            <div className="pb-lbl">Pitch %</div>
            <div className="pb-big" style={{ color: pa >= pg ? 'var(--green)' : 'var(--red)' }}>{pa}%</div>
            <div className="pb-sub">Goal <b>{pg}%</b> &middot; <b>{pitched}</b> pitched of <b>{pitchLeads}</b> leads</div>
            <div className="pb-bar"><i style={{ width: Math.min(100, pa) + '%', background: 'linear-gradient(90deg,#2c456c,#1d3b6c)' }} /></div>
            {edit && <div className="pb-ed">
              <label>Total leads</label><input value={form.pitch_leads ?? ''} onChange={e => set('pitch_leads', e.target.value)} />
              <label>Total pitched</label><input value={form.pitch_pitched ?? ''} onChange={e => set('pitch_pitched', e.target.value)} />
              <label>Goal %</label><input value={form.pitch_goal ?? ''} onChange={e => set('pitch_goal', e.target.value)} />
            </div>}
          </div>

          {/* CLOSE % */}
          <div className={`pb-tile${edit ? ' pb-editing' : ''}`}>
            <div className="pb-lbl">Close %</div>
            <div className="pb-big" style={{ color: ca >= cg ? 'var(--green)' : 'var(--red)' }}>{ca}%</div>
            <div className="pb-sub">Goal <b>{cg}%</b> &middot; <b>{closed}</b> closed of <b>{pitched}</b> pitched</div>
            <div className="pb-bar"><i style={{ width: Math.min(100, ca) + '%', background: 'linear-gradient(90deg,#e6bd5c,#c9922a)' }} /></div>
            {edit && <div className="pb-ed">
              <label>Total closed</label><input value={form.close_closed ?? ''} onChange={e => set('close_closed', e.target.value)} />
              <label>Goal %</label><input value={form.close_goal ?? ''} onChange={e => set('close_goal', e.target.value)} />
            </div>}
          </div>

          {/* DAYS LEFT */}
          <div className={`pb-tile${edit ? ' pb-editing' : ''}`}>
            <div className="pb-lbl">Days Left</div>
            <div className="pb-big">{d.daysLeft}</div>
            <div className="pb-sub">working days this month · Sundays excluded</div>
            {edit && <div className="pb-ed">
              <label>Override (blank = auto)</label><input value={form.days_left_override ?? ''} onChange={e => set('days_left_override', e.target.value)} placeholder="auto" />
            </div>}
          </div>

          {/* MONTHLY GOAL */}
          <div className={`pb-tile${edit ? ' pb-editing' : ''}`}>
            <div className="pb-lbl">Monthly Goal</div>
            <div className="pb-big money">{money0(numv(view.monthly_goal))}</div>
            <div className="pb-sub">Pacing <b>{money0(d.pacing)}</b>
              <span className={`pb-pill ${d.ahead ? 'up' : 'down'}`}>{d.ahead ? '▲ ahead ' : '▼ behind '}{money0(Math.abs(d.diff))}</span>
            </div>
            <div className="pb-sub" style={{ fontSize: '.8rem' }}>Avg {money0(d.avg)}/day × {d.totalDays} working days</div>
            {edit && <div className="pb-ed">
              <label>Monthly goal $</label><input value={form.monthly_goal ?? ''} onChange={e => set('monthly_goal', e.target.value)} />
            </div>}
          </div>
        </div>}
        {tab === 'history' && (
          <HistoryPanel
            rows={history} missing={missing}
            onEdit={(d) => setDayEdit(d)}
            onAdd={() => setDayEdit(todayISO())}
          />
        )}

        {dayEdit && (
          <DayEditor
            date={dayEdit}
            onClose={() => setDayEdit(null)}
            onSaved={() => { setDayEdit(null); loadHistory(); }}
          />
        )}
      </div>
    </div>
  );
}
